import { useState } from 'react';
import { useVideos } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import VideoUploader from './VideoUploader';
import { Button, Select, Card, CardContent, Spinner, Alert } from '@/components/ui';
import { formatFileSize } from '@/utils/formatters';
import type { Video } from '@/types/api';

interface VideoSelectorProps {
  onSelect: (videoId: string) => void;
  selectedVideoId?: string;
  /** Override to force showing/hiding the "Use Existing" option */
  allowExisting?: boolean;
  videoType?: 'template' | 'athlete';
}

function VideoSelector({ onSelect, selectedVideoId, allowExisting, videoType = 'athlete' }: VideoSelectorProps) {
  const { user } = useAuth();
  // "Use Existing Video" is only available to admins (for dev/testing)
  // unless explicitly overridden by the allowExisting prop.
  const showExistingOption = allowExisting ?? user?.role === 'admin';

  const [mode, setMode] = useState<'existing' | 'upload'>('upload');
  const { data: videos, isLoading, error } = useVideos();

  const handleVideoUpload = (videoId: string) => {
    onSelect(videoId);
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const videoId = e.target.value;
    if (videoId) {
      onSelect(videoId);
    }
  };

  const toggleMode = (newMode: 'existing' | 'upload') => {
    setMode(newMode);
    // Clear selection when switching modes
    if (selectedVideoId) {
      onSelect('');
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode Toggle — "Use Existing" only shown for admins (dev/testing) */}
      {showExistingOption && (
        <div className="flex gap-2">
          <Button
            onClick={() => toggleMode('upload')}
            variant={mode === 'upload' ? 'primary' : 'outline'}
            size="sm"
          >
            Upload New Video
          </Button>
          <Button
            onClick={() => toggleMode('existing')}
            variant={mode === 'existing' ? 'primary' : 'outline'}
            size="sm"
          >
            Use Existing Video
          </Button>
        </div>
      )}

      {/* Existing Video Selection */}
      {mode === 'existing' && (
        <div>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" label="Loading videos..." />
            </div>
          )}

          {error && (
            <Alert variant="error">
              Failed to load videos: {error.message}
            </Alert>
          )}

          {!isLoading && !error && videos && videos.length === 0 && (
            <Alert variant="info">
              No videos available. Upload a new video to get started.
            </Alert>
          )}

          {!isLoading && !error && videos && videos.length > 0 && (
            <div className="space-y-3">
              <Select
                value={selectedVideoId || ''}
                onChange={handleVideoSelect}
                className="w-full"
                options={[
                  { value: '', label: 'Select a video...' },
                  ...videos.map((video: Video) => ({
                    value: video.id,
                    label: `${video.filename}${video.duration ? ` (${Math.round(video.duration)}s)` : ''}`,
                  })),
                ]}
              />

              {/* Selected Video Details */}
              {selectedVideoId && videos && (
                <Card>
                  <CardContent className="p-4">
                    {(() => {
                      const selectedVideo = videos.find((v: Video) => v.id === selectedVideoId);
                      if (!selectedVideo) return null;

                      return (
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-text-primary">Selected Video</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-text-secondary">Filename:</span>
                              <p className="font-medium text-text-primary">{selectedVideo.filename}</p>
                            </div>
                            {selectedVideo.duration && (
                              <div>
                                <span className="text-text-secondary">Duration:</span>
                                <p className="font-medium text-text-primary">{Math.round(selectedVideo.duration)}s</p>
                              </div>
                            )}
                            {selectedVideo.file_size && (
                              <div>
                                <span className="text-text-secondary">Size:</span>
                                <p className="font-medium text-text-primary">{formatFileSize(selectedVideo.file_size)}</p>
                              </div>
                            )}
                            {selectedVideo.fps && (
                              <div>
                                <span className="text-text-secondary">FPS:</span>
                                <p className="font-medium text-text-primary">{selectedVideo.fps}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Video Upload */}
      {mode === 'upload' && (
        <VideoUploader videoType={videoType} onUploadComplete={handleVideoUpload} />
      )}
    </div>
  );
}

export default VideoSelector;
